"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.asExpandedQuestionnaire = asExpandedQuestionnaire;
exports.formatRepeatableFieldGroupTitle = formatRepeatableFieldGroupTitle;
exports.appendRepeatableInstancesToId = appendRepeatableInstancesToId;
const lodash_1 = __importDefault(require("lodash"));
const uuid_1 = require("uuid");
const locale_1 = require("./locale");
const structure_1 = require("./structure");
const TransitionVisitor_1 = require("./TransitionVisitor");
const localization_1 = require("../types/localization/localization");
class RepeatableExpansionVisitor extends TransitionVisitor_1.TransitionVisitor {
    answersResolver;
    expandedQuestionnaire;
    repeatedInstanceIdentifierContext;
    constructor(answersResolver) {
        super();
        this.answersResolver = answersResolver;
        this.expandedQuestionnaire = [];
        this.repeatedInstanceIdentifierContext = { byBlueprintId: {}, byNodeId: {} };
    }
    getExpandedQuestionnaire() {
        return this.expandedQuestionnaire;
    }
    visitQuestionnaire(questionnaire) {
        this.expandedQuestionnaire = questionnaire.flatMap((sectionGroup) => {
            const expandableRepeatedSectionGroup = sectionGroup;
            if (expandableRepeatedSectionGroup.options?.repeatable) {
                return this.expandRepeatableQuestionnaireNode(expandableRepeatedSectionGroup);
            }
            return sectionGroup;
        });
        super.visitQuestionnaire(this.expandedQuestionnaire);
    }
    visitSectionGroup(sectionGroup) {
        if (sectionGroup.options?.repeatable) {
            const expandedRepeatableSectionGroup = sectionGroup;
            this.withRepeatedInstanceIdentifierContextFor(expandedRepeatableSectionGroup.nodeId, expandedRepeatableSectionGroup.blueprintId, expandedRepeatableSectionGroup.metadata.repetitionIndex, () => {
                expandedRepeatableSectionGroup.metadata.repeatedInstanceIdentifierContext = {
                    byBlueprintId: { ...this.repeatedInstanceIdentifierContext.byBlueprintId },
                    byNodeId: { ...this.repeatedInstanceIdentifierContext.byNodeId },
                };
                super.visitSectionGroup(sectionGroup);
            });
        }
        else {
            sectionGroup.metadata = {
                repeatedInstanceIdentifierContext: { ...this.repeatedInstanceIdentifierContext },
            };
            super.visitSectionGroup(sectionGroup);
        }
    }
    visitSection(section) {
        section.metadata = {
            repeatedInstanceIdentifierContext: { ...this.repeatedInstanceIdentifierContext },
        };
        super.visitSection(section);
    }
    visitSubsection(subsection) {
        subsection.metadata = {
            repeatedInstanceIdentifierContext: { ...this.repeatedInstanceIdentifierContext },
        };
        subsection.fieldGroups = subsection.fieldGroups.flatMap((fieldGroup) => {
            const repeatedExpandableFieldGroup = fieldGroup;
            if (repeatedExpandableFieldGroup.options?.repeatable) {
                return this.expandRepeatableQuestionnaireNode(repeatedExpandableFieldGroup);
            }
            return fieldGroup;
        });
        super.visitSubsection(subsection);
    }
    visitFieldGroup(fieldGroup) {
        if (fieldGroup.options?.repeatable) {
            const repeatedExpandableFieldGroup = fieldGroup;
            this.visitRepeatedFieldGroup(repeatedExpandableFieldGroup);
            this.withRepeatedInstanceIdentifierContextFor(repeatedExpandableFieldGroup.nodeId, repeatedExpandableFieldGroup.blueprintId, repeatedExpandableFieldGroup.metadata.repetitionIndex, () => {
                fieldGroup.metadata = {
                    ...repeatedExpandableFieldGroup.metadata,
                    repeatedInstanceIdentifierContext: {
                        byBlueprintId: { ...this.repeatedInstanceIdentifierContext.byBlueprintId },
                        byNodeId: { ...this.repeatedInstanceIdentifierContext.byNodeId },
                    },
                };
                super.visitFieldGroup(fieldGroup);
            });
        }
        else {
            fieldGroup.metadata = {
                repeatedInstanceIdentifierContext: { ...this.repeatedInstanceIdentifierContext },
            };
            super.visitFieldGroup(fieldGroup);
        }
    }
    visitRepeatedFieldGroup(fieldGroup) {
        const expandedFieldGroup = fieldGroup;
        const index = expandedFieldGroup.metadata.repetitionIndex;
        const repetitions = expandedFieldGroup.metadata.repetitionCount;
        if ((0, structure_1.isRepeatableOptionsBasedOnCollection)(expandedFieldGroup.options)) {
            Object.assign(expandedFieldGroup, {
                showRemoveQuestionButton: false,
                showAddQuestionButton: false,
            });
            return;
        }
        const hasReachedMinimumRepetitions = repetitions === expandedFieldGroup.options.minRepetitions;
        const isLastRepetition = index === repetitions - 1;
        const hasRepetitionsLeft = index < expandedFieldGroup.options.maxRepetitions - 1;
        Object.assign(expandedFieldGroup, {
            title: formatRepeatableFieldGroupTitle(expandedFieldGroup.title, index),
            showRemoveQuestionButton: !hasReachedMinimumRepetitions,
            showAddQuestionButton: isLastRepetition && hasRepetitionsLeft,
        });
    }
    visitField(field) {
        const expandedField = field;
        if (!expandedField.metadata?.repeatedInstanceIdentifierContext) {
            if (!expandedField.metadata)
                expandedField.metadata = { repeatedInstanceIdentifierContext: { byBlueprintId: {}, byNodeId: {} } };
            expandedField.metadata.repeatedInstanceIdentifierContext = { ...this.repeatedInstanceIdentifierContext };
        }
        // Repeatable option fields rely on containing field.id-s being unique (TODO: figure out why and find a better way)
        field.id = appendRepeatableInstancesToId(field.id, expandedField.metadata.repeatedInstanceIdentifierContext.byNodeId);
        super.visitField(field);
    }
    visitOption(option) {
        option.metadata = {
            repeatedInstanceIdentifierContext: { ...this.repeatedInstanceIdentifierContext },
        };
    }
    expandBasedOnAnotherCollectionAnswerCount(repeatableNode) {
        const { options } = repeatableNode;
        if (!(0, structure_1.isRepeatableOptionsBasedOnCollection)(options)) {
            return [repeatableNode];
        }
        const collectionNodeId = options.expandToCollectionLength; // Expand to collection length is using nodeIds.
        if (!collectionNodeId) {
            return [repeatableNode];
        }
        const collection = this.answersResolver.usingNodeId().getCollection(collectionNodeId);
        if (!collection) {
            return [repeatableNode];
        }
        const selectTitleNodeIds = options.selectTitle || [];
        const selectTextNodeIds = options.selectText || [];
        const hasDefinedSelectTitle = !!options.selectTitle && options.selectTitle.length > 0;
        const hasDefinedSelectText = !!options.selectText && options.selectText.length > 0;
        const f = findSelectValues(this.answersResolver, this.repeatedInstanceIdentifierContext.byNodeId);
        const titleValues = f(collectionNodeId, selectTitleNodeIds); // TODO: they are using nodeIds in the title select
        const textValues = f(collectionNodeId, selectTextNodeIds); // TODO: they are using nodeIds in the title select
        return collection.map((collectionItem, index) => {
            const clonedNode = Object.assign(lodash_1.default.cloneDeep(repeatableNode), {
                id: `${repeatableNode.id}.${index}`,
                // Believe us, right here its just a string, not a localizable thing.
                // We are after the localization has been applied to `localizable` is just string here.
                // Changing the type will have to be done in another PR, hence the following ts-ignores.
                title: hasDefinedSelectTitle
                    ? // @ts-ignore
                        textSubstitution(repeatableNode.title, titleValues[index], '_')
                    : repeatableNode.title,
                text: hasDefinedSelectText
                    ? // @ts-ignore
                        textSubstitution(repeatableNode.text, textValues[index], '_')
                    : repeatableNode.text,
                metadata: {
                    repetitionIndex: index,
                    repetitionCount: collection.length,
                    parentId: repeatableNode.id,
                },
            });
            if ((0, structure_1.isFieldGroup)(repeatableNode) && (0, structure_1.isFieldGroup)(clonedNode)) {
                clonedNode.fields = repeatableNode.fields.map((field) => {
                    return (0, structure_1.isOptionField)(field)
                        ? {
                            ...field,
                            options: field.options.map((option) => ({ ...option, surrogateId: collectionItem.surrogateId })),
                        }
                        : field;
                });
            }
            return clonedNode;
        });
    }
    expandBasedOnMinMaxLimit(repeatableNode) {
        const { blueprintId, options } = repeatableNode;
        if (!(0, structure_1.isRepeatableOptionsWithLimits)(options)) {
            return [repeatableNode];
        }
        const repetitionCount = this.findRepetitionsCount(blueprintId, options.minRepetitions);
        return lodash_1.default.times(repetitionCount, (repetitionIndex) => {
            const surrogateId = this.surrogateIdFor(blueprintId, {
                ...this.repeatedInstanceIdentifierContext.byBlueprintId,
                [blueprintId]: repetitionIndex,
            });
            return Object.assign(lodash_1.default.cloneDeep(repeatableNode), {
                id: `${repeatableNode.id}.${repetitionIndex}`,
                surrogateId: surrogateId,
                options: {
                    ...repeatableNode.options,
                },
                metadata: {
                    repetitionIndex,
                    repetitionCount,
                    parentId: repeatableNode.id,
                },
            });
        });
    }
    expandRepeatableQuestionnaireNode(repeatableNode) {
        const { options } = repeatableNode;
        if ((0, structure_1.isRepeatableOptionsBasedOnCollection)(options)) {
            return this.expandBasedOnAnotherCollectionAnswerCount(repeatableNode);
        }
        else {
            return this.expandBasedOnMinMaxLimit(repeatableNode);
        }
    }
    findRepetitionsCount(blueprintId, minRepetitions) {
        const repetitions = this.answersResolver.getRepetitionCount(blueprintId, this.repeatedInstanceIdentifierContext.byBlueprintId);
        return repetitions ?? minRepetitions;
    }
    withRepeatedInstanceIdentifierContextFor(nodeId, blueprintId, instancePath, callback) {
        this.enterRepeatedInstanceIdentifierContext(nodeId, blueprintId, instancePath);
        callback();
        this.exitRepeatedInstanceIdentifierContext(nodeId, blueprintId);
    }
    enterRepeatedInstanceIdentifierContext(nodeId, blueprintId, instancePath) {
        this.repeatedInstanceIdentifierContext.byNodeId[nodeId] = instancePath;
        this.repeatedInstanceIdentifierContext.byBlueprintId[blueprintId] = instancePath;
    }
    exitRepeatedInstanceIdentifierContext(nodeId, blueprintId) {
        delete this.repeatedInstanceIdentifierContext.byNodeId[nodeId];
        delete this.repeatedInstanceIdentifierContext.byBlueprintId[blueprintId];
    }
    surrogateIdFor(blueprintId, repeatedInstanceIdentifierContext) {
        const existingSurrogateId = this.answersResolver.getAnswer(blueprintId, repeatedInstanceIdentifierContext)?.surrogateId;
        return existingSurrogateId || (0, uuid_1.v4)();
    }
}
function findSelectValues(answersResolver, repeatedInstanceIdentifierContext) {
    return (collectionNodeId, selectTitleNodeIds) => {
        if (selectTitleNodeIds.length === 0) {
            return [];
        }
        const answersBySurrogateId = answersResolver
            .usingNodeId()
            .getRepeatedAnswers(collectionNodeId, selectTitleNodeIds, repeatedInstanceIdentifierContext);
        if (!answersBySurrogateId) {
            return [];
        }
        return Object.keys(answersBySurrogateId).map((key) => selectTitleNodeIds.map((nodeId) => answersBySurrogateId[key].answersByNodeId[nodeId]));
    };
}
function formatRepeatableFieldGroupTitle(title, index) {
    const replaceIndex = (str) => str.replace('%{index}', (index + 1).toString());
    switch (typeof title) {
        case 'string':
            return replaceIndex(title);
        case 'object':
            return {
                en: replaceIndex((0, locale_1.localize)(title, localization_1.Language.en)),
                fr: replaceIndex((0, locale_1.localize)(title, localization_1.Language.fr)),
            };
    }
}
function appendRepeatableInstancesToId(id, repeatedIdentifiers) {
    const orderedIdentifiers = Object.entries(repeatedIdentifiers).sort(([aKey], [bKey]) => (aKey > bKey ? 1 : -1));
    const identifierStrings = orderedIdentifiers.map(([key, value]) => `${key}_${value}`);
    return identifierStrings.length ? `${id}_${identifierStrings.join('_')}` : id;
}
function asExpandedQuestionnaire(questionnaire, answersResolver) {
    const repeatableExpansionVisitor = new RepeatableExpansionVisitor(answersResolver);
    repeatableExpansionVisitor.visitQuestionnaire(questionnaire);
    return repeatableExpansionVisitor.getExpandedQuestionnaire();
}
function textSubstitution(text, words, fallbackWord) {
    if (typeof text !== 'string') {
        return fallbackWord;
    }
    const wordsWithFallback = words.map((e) => (typeof e === 'string' ? e : fallbackWord));
    let i = 0;
    return text.replace(/{}/g, function () {
        return typeof wordsWithFallback[i] != 'undefined' ? wordsWithFallback[i++] : fallbackWord;
    });
}
